import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuthStore } from '@/stores/authStore'
import { apiService } from '@/services/api'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Loader2, Plus, Edit, Trash2, Calendar, User, Lock } from 'lucide-react'

const ConsultantNotes = () => {
  const { companyUrl, user } = useAuthStore()
  const { toast } = useToast()
  const [notes, setNotes] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [viewingNote, setViewingNote] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [formData, setFormData] = useState({ title: '', description: '', clientId: '', isPrivate: false })
  const [editingNote, setEditingNote] = useState(null)
  const [selectedNoteIds, setSelectedNoteIds] = useState([])
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [shareTarget, setShareTarget] = useState('')
  const [consultants, setConsultants] = useState([])
  const [shareLoading, setShareLoading] = useState(false)

  const consultantId = useMemo(() => {
    return user?.id || user?.userId || user?.user_id || user?.consultant_id || ''
  }, [user])

  const loadNotes = async () => {
    if (!companyUrl || !consultantId) return
    setLoading(true)
    setError('')
    try {
      const result = await apiService.get(`/${companyUrl}/notes/getConsultantNotes/${consultantId}`)
      if (result.success) {
        setNotes(Array.isArray(result.data) ? result.data : [])
      } else {
        setError(result.error || 'Failed to load notes')
      }
    } catch (err) {
      setError(err?.message || 'Failed to load notes')
    } finally {
      setLoading(false)
    }
  }

  const loadClients = async () => {
    if (!companyUrl) return
    try {
      const result = await apiService.post(`/${companyUrl}/fetchloggedinconsultantclients`, {})
      if (result.success) {
        const rows = Array.isArray(result.data?.rows) ? result.data.rows : Array.isArray(result.data) ? result.data : []
        setClients(rows)
      }
    } catch (err) {
      console.error(err)
    }
  }

  // Notes are shared with other coaches, so the share list comes from the
  // company's consultants (it used to list clients and send their ids).
  const loadConsultants = async () => {
    if (!companyUrl) return
    try {
      const result = await apiService.post(`/${companyUrl}/companyadmin/getconsultants`, {
        fromIndex: 0, limit: 200, orderBy: 'ASC', orderByOn: 'firstname', searchtext: '',
      })
      if (result.success) {
        const rows = Array.isArray(result.data?.rows) ? result.data.rows : Array.isArray(result.data) ? result.data : []
        setConsultants(rows.filter((c) => String(c.id) !== String(consultantId)))
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    loadNotes()
    loadClients()
    loadConsultants()
  }, [companyUrl, consultantId])

  const resetForm = () => {
    setFormData({ title: '', description: '', clientId: '', isPrivate: false })
    setEditingNote(null)
  }

  const openNewModal = () => {
    resetForm()
    setModalOpen(true)
  }

  const toggleSelect = (noteId) => {
    setSelectedNoteIds((prev) => prev.includes(noteId) ? prev.filter(id => id !== noteId) : [...prev, noteId])
  }

  const getClientName = (clientId) => {
    if (!clientId) return null
    const client = clients.find((c) => String(c.id) === String(clientId))
    if (!client) return null
    return client.email || [client.firstname, client.lastname].filter(Boolean).join(' ') || `Client ${client.id}`
  }

  const openEditModal = (note) => {
    setEditingNote(note)
    setFormData({
      title: note?.notes_v2?.name || '',
      description: note?.notes_v2?.description || '',
      clientId: note?.notes_v2?.client_id ? String(note.notes_v2.client_id) : '',
      isPrivate: Boolean(note?.notes_v2?.is_private)
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!companyUrl || !consultantId) {
      toast({ title: 'Missing info', description: 'Company or user not available', variant: 'destructive' })
      return
    }
    if (!formData.title.trim() || !formData.description.trim()) {
      toast({ title: 'Required fields', description: 'Title and description are required', variant: 'destructive' })
      return
    }

    setSaving(true)
    const payload = {
      name: formData.title.trim(),
      description: formData.description.trim(),
      client_id: formData.clientId || null,
      is_private: Boolean(formData.isPrivate)
    }

    const endpoint = editingNote
      ? `/${companyUrl}/notes/updateConsultantNote/${editingNote.note_id}`
      : `/${companyUrl}/notes/createclientnote/${consultantId}`

    try {
      const result = await apiService.post(endpoint, payload)
      if (result.success) {
        toast({ title: 'Saved', description: 'Note saved successfully' })
        setModalOpen(false)
        resetForm()
        loadNotes()
      } else {
        toast({ title: 'Save failed', description: result.error || 'Could not save note', variant: 'destructive' })
      }
    } catch (err) {
      toast({ title: 'Save failed', description: err?.message || 'Could not save note', variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget || !companyUrl) return
    setDeleting(true)
    try {
      const endpoint = `/${companyUrl}/notes/deletenote/${deleteTarget.note_id}`
      const result = await apiService.delete(endpoint)
      if (result.success) {
        toast({ title: 'Deleted', description: 'Note deleted' })
        setDeleteTarget(null)
        loadNotes()
      } else {
        toast({ title: 'Delete failed', description: result.error || 'Could not delete note', variant: 'destructive' })
      }
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.message || 'Could not delete note', variant: 'destructive' })
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (value) => {
    if (!value) return '—'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  }

  const handleShare = async () => {
    if (!selectedNoteIds.length || !shareTarget) {
      toast({ title: 'Missing selection', description: 'Select at least one note and a coach', variant: 'destructive' })
      return
    }
    if (!companyUrl) return
    setShareLoading(true)
    try {
      const payload = {
        consultant_ids: [Number(shareTarget)],
        note_ids: selectedNoteIds
      }
      const result = await apiService.post(`/${companyUrl}/notes/assignnotestoconsultants`, payload)
      if (result.success) {
        toast({ title: 'Shared', description: 'Notes shared successfully' })
        setShareModalOpen(false)
        setShareTarget('')
        setSelectedNoteIds([])
      } else {
        toast({ title: 'Share failed', description: result.error || 'Could not share notes', variant: 'destructive' })
      }
    } catch (err) {
      toast({ title: 'Share failed', description: err?.message || 'Could not share notes', variant: 'destructive' })
    } finally {
      setShareLoading(false)
    }
  }

  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading notes...
        </div>
      )
    }

    if (error) {
      return (
        <div className="text-center py-12 text-red-600">{error}</div>
      )
    }

    if (!notes.length) {
      return (
        <div className="text-center py-12 text-gray-500">No notes found</div>
      )
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">Select</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created On</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {notes.map((note) => (
              <tr
                key={note.note_id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => setViewingNote(note)}
              >
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={selectedNoteIds.includes(note.note_id)}
                    onChange={() => toggleSelect(note.note_id)}
                  />
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{note?.notes_v2?.name || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{note?.notes_v2?.description || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{formatDate(note.created_on)}</td>
                <td className="px-4 py-3 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" onClick={() => openEditModal(note)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setDeleteTarget(note)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Notes</h1>
          <p className="text-sm text-gray-500">Consultant notes for your clients</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedNoteIds.length > 0 && (
            <Button variant="outline" onClick={() => setShareModalOpen(true)}>
              Share
            </Button>
          )}
          <Button onClick={openNewModal} className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Add Notes</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          {renderTable()}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingNote ? `Edit Notes : ${editingNote?.notes_v2?.name || ''}` : 'Add Notes'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Select Client</label>
              <Select
                value={formData.clientId}
                onValueChange={(val) => setFormData((prev) => ({ ...prev, clientId: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={String(client.id)}>
                      {client.email || [client.firstname, client.lastname].filter(Boolean).join(' ') || `Client ${client.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-3 pt-2">
              <Switch
                checked={formData.isPrivate}
                onCheckedChange={(val) => setFormData((prev) => ({ ...prev, isPrivate: val }))}
              />
              <span className="text-sm text-gray-800">Mark as Private</span>
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-end space-x-3">
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(viewingNote)} onOpenChange={(open) => !open && setViewingNote(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-6">
              <DialogTitle className="text-xl">{viewingNote?.notes_v2?.name || 'Note'}</DialogTitle>
              {viewingNote?.notes_v2?.is_private && (
                <Badge variant="secondary" className="flex shrink-0 items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Private
                </Badge>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {formatDate(viewingNote?.created_on)}
              </span>
              {getClientName(viewingNote?.notes_v2?.client_id) && (
                <span className="flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  {getClientName(viewingNote?.notes_v2?.client_id)}
                </span>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Description</label>
              <div className="mt-1.5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
                  {viewingNote?.notes_v2?.description || '—'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-end space-x-3">
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => {
                setDeleteTarget(viewingNote)
                setViewingNote(null)
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                openEditModal(viewingNote)
                setViewingNote(null)
              }}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
            <Button onClick={() => setViewingNote(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmation</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-gray-700">Are you sure you want to delete the notes?</div>
          <DialogFooter className="flex flex-row justify-end space-x-3">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ok
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareModalOpen} onOpenChange={(open) => {
        setShareModalOpen(open)
        if (!open) {
          setShareTarget('')
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Notes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Share with coach</label>
              <Select value={shareTarget} onValueChange={setShareTarget}>
                <SelectTrigger>
                  <SelectValue placeholder={consultants.length ? 'Select coach' : 'No other coaches in this company'} />
                </SelectTrigger>
                <SelectContent>
                  {consultants.map((coach) => (
                    <SelectItem key={coach.id} value={String(coach.id)}>
                      {[coach.firstname, coach.lastname].filter(Boolean).join(' ') || coach.email || `Coach ${coach.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-end space-x-3">
            <Button variant="outline" onClick={() => setShareModalOpen(false)} disabled={shareLoading}>
              Cancel
            </Button>
            <Button onClick={handleShare} disabled={shareLoading}>
              {shareLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ConsultantNotes
